/**
 * Tenant → Invoice Email Settings
 *
 * Configure auto-send on invoice creation, default subject/body templates,
 * CC-firm-owner toggle, payment-link attachment toggle.
 */
import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, Mail, BookOpen, Zap } from "lucide-react";
import { GoldButton, PageHeader, Field, Input, Textarea } from "@/components/Ui";

const TOKENS = [
    { k: "{{client_name}}",    d: "Recipient's name from client record" },
    { k: "{{invoice_number}}", d: "e.g. INV-00042" },
    { k: "{{amount}}",         d: "Invoice total in ₹" },
    { k: "{{due_date}}",       d: "Due date (or — if none)" },
    { k: "{{firm_name}}",      d: "Your firm's name (or override below)" },
    { k: "{{firm_email}}",     d: "Your firm's billing email" },
    { k: "{{payment_link}}",   d: "Short URL to pay online (auto if enabled)" },
];

export default function InvoiceEmailSettingsPage() {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try { const r = await api.get("/tenant/invoice-email-settings"); setForm(r.data); }
        catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (!form) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

    const save = async () => {
        setSaving(true);
        try { await api.patch("/tenant/invoice-email-settings", form); toast.success("Saved"); load(); }
        catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <>
            <PageHeader title="Invoice Email Settings" subtitle="Auto-send new invoices to clients · default subject & body · CC-yourself · attach payment link." action={
                <GoldButton onClick={save} disabled={saving} data-testid="save"><Save className="w-4 h-4 inline mr-1"/>{saving ? "Saving…" : "Save"}</GoldButton>
            }/>

            <div className="border border-primary/30 bg-primary/5 p-5 mb-6" data-testid="explainer">
                <div className="flex items-center gap-2 mb-3"><BookOpen className="w-4 h-4 text-primary"/><div className="text-sm font-medium">Template tokens</div></div>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                    {TOKENS.map(t => (
                        <div key={t.k}><code className="text-primary">{t.k}</code> <span className="text-muted-foreground">— {t.d}</span></div>
                    ))}
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-card border border-border p-5 space-y-4">
                    <div className="overline">Behaviour</div>
                    <label className="flex items-center gap-2 cursor-pointer p-3 border border-border hover:border-primary" data-testid="auto-send-toggle">
                        <input type="checkbox" checked={!!form.auto_send_on_create}
                            onChange={e => setForm({...form, auto_send_on_create: e.target.checked})}/>
                        <Zap className={`w-4 h-4 ${form.auto_send_on_create ? "text-primary" : "text-muted-foreground"}`}/>
                        <div className="flex-1 text-sm">
                            <div>Auto-send when an invoice is created</div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">Sends to the client's email automatically. Failures don't block the invoice — they're logged in the email history.</div>
                        </div>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={!!form.cc_firm_owner}
                            onChange={e => setForm({...form, cc_firm_owner: e.target.checked})} data-testid="cc-owner"/>
                        <span className="text-sm">CC the firm owner on every invoice email</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={!!form.attach_payment_link}
                            onChange={e => setForm({...form, attach_payment_link: e.target.checked})} data-testid="attach-link"/>
                        <span className="text-sm">Include an online payment link in the body</span>
                    </label>
                    <Field label='"From" name (optional override)'>
                        <Input value={form.from_name || ""} onChange={e => setForm({...form, from_name: e.target.value})}
                            placeholder="Defaults to your firm name" data-testid="from-name"/>
                    </Field>
                </div>

                <div className="bg-card border border-border p-5 space-y-4">
                    <div className="overline">Templates</div>
                    <Field label="Subject template">
                        <Input value={form.subject_template || ""} onChange={e => setForm({...form, subject_template: e.target.value})} data-testid="subject-template"/>
                    </Field>
                    <Field label="Body template (plain text · multi-line OK)">
                        <Textarea rows={10} value={form.body_template || ""} onChange={e => setForm({...form, body_template: e.target.value})} data-testid="body-template"/>
                    </Field>
                </div>
            </div>

            <div className="mt-6 border border-border bg-background p-4" data-testid="preview-card">
                <div className="flex items-center gap-2 mb-3"><Mail className="w-4 h-4 text-primary"/><div className="text-sm font-medium">Preview (with sample data)</div></div>
                <Preview form={form}/>
            </div>
        </>
    );
}

function Preview({ form }) {
    const ctx = {
        client_name: "Reliance Industries Ltd",
        invoice_number: "INV-00042",
        amount: "1,25,000.00",
        due_date: "2026-07-15",
        firm_name: form.from_name || "Acme Law Chambers",
        firm_email: "billing@acmelaw.in",
        payment_link: form.attach_payment_link ? "Pay online here: https://lawos.in/pay/xyz123" : "",
    };
    const interp = (s) => Object.entries(ctx).reduce((acc, [k, v]) => acc.split("{{" + k + "}}").join(v), s || "");
    return (
        <div className="font-mono text-[11px]">
            <div className="text-muted-foreground">Subject:</div>
            <div className="text-foreground mb-3 ml-3">{interp(form.subject_template)}</div>
            <div className="text-muted-foreground">Body:</div>
            <pre className="text-foreground ml-3 whitespace-pre-wrap">{interp(form.body_template)}</pre>
        </div>
    );
}
